<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import { Link, router } from '@inertiajs/svelte';
    import { SvelteDate } from 'svelte/reactivity';
    import { scale } from 'svelte/transition';
    import { flip } from 'svelte/animate';
    import Modal from '../../components/Modal.svelte';
    import ConfirmModal from '../../components/ConfirmModal.svelte';
    import { show, schedule as scheduleRoute } from '@/routes/admin/cases';
    import { move, remove } from '@/routes/admin/schedule';
    import { formatDate as fmtDate } from '../../utils/dates';

    interface ScheduledCase {
        id: number;
        schedule_id: number;
        history_excerpt: string;
        topic_name: string | null;
        image_url: string | null;
    }

    interface SearchResult {
        id: number;
        history_excerpt: string;
        topic_name: string | null;
        scheduled_dates: string[];
    }

    interface TopicOption {
        id: number;
        name: string;
        disabled: boolean;
    }

    let { schedule: scheduleData, startDate, endDate, today, topics, topicId }: {
        schedule: Record<string, ScheduledCase[]>;
        startDate: string;
        endDate: string;
        today: string;
        topics: TopicOption[];
        topicId: number | null;
    } = $props();

    function buildUrl(overrideTopicId?: number | null): string {
        const params = new URLSearchParams();
        const tid = overrideTopicId !== undefined ? overrideTopicId : topicId;
        if (tid) params.set('topic_id', String(tid));
        if (startDate) params.set('start', startDate);
        if (endDate) params.set('end', endDate);
        const qs = params.toString();
        return '/admin/schedule' + (qs ? `?${qs}` : '');
    }

    function filterByTopic(id: number | null) {
        const next = topicId === id ? null : id;
        router.visit(buildUrl(next), {
            preserveState: true,
            preserveScroll: true,
        });
    }

    function loadMore(days: number = 28) {
        const end = new SvelteDate(endDate + 'T12:00:00');
        end.setDate(end.getDate() + days);
        const params = new URLSearchParams();
        if (topicId) params.set('topic_id', String(topicId));
        if (startDate) params.set('start', startDate);
        params.set('end', end.toISOString().split('T')[0]);
        router.visit('/admin/schedule?' + params.toString(), {
            preserveState: true,
            preserveScroll: true,
        });
    }

    function loadEarlier(days: number = 7) {
        const start = new SvelteDate(startDate + 'T12:00:00');
        start.setDate(start.getDate() - days);
        const params = new URLSearchParams();
        if (topicId) params.set('topic_id', String(topicId));
        params.set('start', start.toISOString().split('T')[0]);
        if (endDate) params.set('end', endDate);
        router.visit('/admin/schedule?' + params.toString(), {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => { pastDaysToShow += days; },
        });
    }

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // Agenda view — respect server-loaded date range
    const pastDates = $derived(Object.keys(scheduleData).filter(d => d < today).sort());
    const pastDayCount = $derived(pastDates.length);

    // If the server loaded dates before today (via start param), show them by default
    const initialPastDays = startDate < today ? pastDayCount : 0;
    let pastDaysToShow = $state(initialPastDays);
    const hiddenPastCount = $derived(pastDayCount - pastDaysToShow);

    // Show all server-loaded future days, or at least 7
    const initialFutureDays = Math.max(7, Object.keys(scheduleData).filter(d => d >= today).length);
    let agendaDaysToShow = $state(initialFutureDays);

    // Sorted dates grouped by month
    const allDates = $derived(Object.keys(scheduleData).sort());

    interface AgendaMonth {
        label: string;
        dates: string[];
    }

    const futureDates = $derived(allDates.filter(d => d >= today));
    const totalFutureDays = $derived(futureDates.length);
    const remainingDays = $derived(totalFutureDays - agendaDaysToShow);

    const agendaMonths = $derived(() => {
        const visiblePast = pastDaysToShow > 0 ? pastDates.slice(-pastDaysToShow) : [];
        const visibleFuture = futureDates.slice(0, agendaDaysToShow);
        const filteredDates = [...visiblePast, ...visibleFuture];
        const months: AgendaMonth[] = [];
        let current: AgendaMonth | null = null;
        for (const date of filteredDates) {
            const d = new Date(date + 'T12:00:00');
            const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            if (!current || current.label !== label) {
                current = { label, dates: [] };
                months.push(current);
            }
            current.dates.push(date);
        }
        return months;
    });

    function agendaDayLabel(dateStr: string): { day: string; weekday: string; isWeekend: boolean } {
        const d = new Date(dateStr + 'T12:00:00');
        return {
            day: String(d.getDate()),
            weekday: dayNames[d.getDay() === 0 ? 6 : d.getDay() - 1],
            isWeekend: d.getDay() === 0 || d.getDay() === 6,
        };
    }

    const weeks = $derived(() => {
        const dates = Object.keys(scheduleData).sort();
        const result: string[][] = [];
        for (let i = 0; i < dates.length; i += 7) {
            result.push(dates.slice(i, i + 7));
        }
        return result;
    });

    function formatDateFull(dateStr: string): string {
        return fmtDate(dateStr, { month: 'short', day: 'numeric' });
    }

    function formatDate(dateStr: string, sectionMonth: number): string {
        const d = new Date(dateStr + 'T12:00:00');
        if (d.getMonth() !== sectionMonth) {
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return String(d.getDate());
    }

    function isPast(dateStr: string): boolean {
        return dateStr < today;
    }

    function getMonth(dateStr: string): number {
        return new Date(dateStr + 'T12:00:00').getMonth();
    }

    function newMonthInWeek(week: string[], weekIndex: number, allWeeks: string[][]): string | null {
        if (weekIndex === 0) {
            return null;
        }
        const prevWeekLastMonth = getMonth(allWeeks[weekIndex - 1][allWeeks[weekIndex - 1].length - 1]);
        for (const date of week) {
            if (getMonth(date) !== prevWeekLastMonth) {
                const d = new Date(date + 'T12:00:00');
                return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            }
        }
        return null;
    }

    function sectionMonthForWeek(weekIndex: number, allWeeks: string[][]): number {
        for (let i = weekIndex; i >= 0; i--) {
            if (i === 0 || newMonthInWeek(allWeeks[i], i, allWeeks)) {
                const week = allWeeks[i];
                for (const date of week) {
                    const m = getMonth(date);
                    if (i === 0 || m !== getMonth(allWeeks[i - 1][allWeeks[i - 1].length - 1])) {
                        return m;
                    }
                }
            }
        }
        return getMonth(allWeeks[0][0]);
    }

    // Track known schedule_ids for animating new entries
    let knownScheduleIds = $state<Set<number>>(new Set(
        Object.values(scheduleData).flat().map(c => c.schedule_id)
    ));
    let newScheduleIds = $state<Set<number>>(new Set());
    function isNew(scheduleId: number): boolean {
        return newScheduleIds.has(scheduleId);
    }

    // Modal state
    let modalOpen = $state(false);
    let selectedDate = $state('');
    let searchQuery = $state('');
    let searchResults = $state<SearchResult[]>([]);
    let searching = $state(false);
    let scheduling = $state(false);
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    function openAddModal(date: string) {
        selectedDate = date;
        searchQuery = '';
        searchResults = [];
        modalOpen = true;
        performSearch('');
    }

    function performSearch(query: string) {
        searching = true;
        fetch(`/admin/schedule/search-cases?search=${encodeURIComponent(query)}`, {
            headers: { 'Accept': 'application/json' },
        })
            .then(res => res.json())
            .then(data => {
                searchResults = data.data;
                searching = false;
            })
            .catch(() => {
                searching = false;
            });
    }

    function handleSearchInput() {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        searchTimeout = setTimeout(() => performSearch(searchQuery), 300);
    }

    function scheduleCase(caseId: number) {
        scheduling = true;
        const previousIds = new Set(knownScheduleIds);
        router.post(scheduleRoute.url(caseId), { dates: [selectedDate] }, {
            preserveScroll: true,
            only: ['schedule'],
            invalidateCacheTags: ['admin-cases'],
            onSuccess: () => {
                modalOpen = false;
                scheduling = false;
                const allIds = Object.values(scheduleData).flat().map(c => c.schedule_id);
                const added = new Set(allIds.filter(id => !previousIds.has(id)));
                newScheduleIds = added;
                knownScheduleIds = new Set(allIds);
                if (added.size > 0) {
                    setTimeout(() => { newScheduleIds = new Set(); }, 1000);
                }
            },
            onError: () => {
                scheduling = false;
            },
        });
    }

    // Drag and drop state
    let dragScheduleId = $state<number | null>(null);
    let dragCaseId = $state<number | null>(null);
    let dragFromDate = $state<string | null>(null);
    let dropTargetDate = $state<string | null>(null);

    function handleDragStart(e: DragEvent, c: ScheduledCase, fromDate: string) {
        dragScheduleId = c.schedule_id;
        dragCaseId = c.id;
        dragFromDate = fromDate;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
        }
    }

    function handleDragEnd() {
        dragScheduleId = null;
        dragCaseId = null;
        dragFromDate = null;
        dropTargetDate = null;
    }

    function isCaseOnDate(caseId: number, date: string): boolean {
        return (scheduleData[date] ?? []).some(c => c.id === caseId);
    }

    function isValidDropTarget(date: string): boolean {
        return !!dragScheduleId && !!dragCaseId && !isPast(date) && date !== dragFromDate && !isCaseOnDate(dragCaseId, date);
    }

    function handleDragOver(e: DragEvent, date: string) {
        if (!isValidDropTarget(date)) {
            return;
        }
        e.preventDefault();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
        }
        dropTargetDate = date;
    }

    function handleDragLeave(e: DragEvent, date: string) {
        if (dropTargetDate === date) {
            dropTargetDate = null;
        }
    }

    function executeMove(scheduleId: number, date: string) {
        router.patch(move(scheduleId), { date }, {
            preserveScroll: true,
            only: ['schedule'],
        });
    }

    // Move confirmation state (for dragging from today)
    let moveConfirmOpen = $state(false);
    let pendingMoveScheduleId = $state<number | null>(null);
    let pendingMoveDate = $state('');

    function handleDrop(e: DragEvent, date: string) {
        e.preventDefault();
        dropTargetDate = null;

        if (!dragScheduleId || !isValidDropTarget(date)) {
            return;
        }

        if (dragFromDate === today) {
            pendingMoveScheduleId = dragScheduleId;
            pendingMoveDate = date;
            moveConfirmOpen = true;
        } else {
            executeMove(dragScheduleId, date);
        }

        dragScheduleId = null;
        dragCaseId = null;
        dragFromDate = null;
    }

    function confirmMove() {
        if (pendingMoveScheduleId) {
            executeMove(pendingMoveScheduleId, pendingMoveDate);
            pendingMoveScheduleId = null;
        }
    }

    // Remove case date state
    let removeConfirmOpen = $state(false);
    let removeScheduleId = $state<number | null>(null);
    let removeDate = $state('');
    let removeConfirmTitle = $state('');
    let removeConfirmMessage = $state('');
    let removeVariant = $state<'danger' | 'warning'>('warning');

    function promptRemove(e: MouseEvent, scheduleId: number, date: string) {
        e.preventDefault();
        e.stopPropagation();
        removeScheduleId = scheduleId;
        removeDate = date;
        const isToday = date === today;
        removeVariant = isToday ? 'danger' : 'warning';
        removeConfirmTitle = isToday ? 'Remove today\'s case?' : 'Remove from schedule?';
        removeConfirmMessage = isToday
            ? 'This case is scheduled for TODAY. Users may have already seen it and submitted answers. Removing it cannot be easily undone.'
            : `Remove this case from ${formatDateFull(date)}?`;
        removeConfirmOpen = true;
    }

    function explodeElement(el: HTMLElement): Promise<void> {
        const rect = el.getBoundingClientRect();
        const container = document.createElement('div');
        container.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:9999;overflow:visible;';
        document.body.appendChild(container);

        el.style.visibility = 'hidden';

        // Build a jagged vertical fracture line down the middle
        const segments = 8;
        const segH = rect.height / segments;
        const midX = rect.width * (0.45 + Math.random() * 0.1);
        const jagPoints: { x: number; y: number }[] = [];
        for (let i = 0; i <= segments; i++) {
            const jag = i === 0 || i === segments ? 0 : (Math.random() - 0.5) * rect.width * 0.25;
            jagPoints.push({ x: midX + jag, y: i * segH });
        }

        // Left half: top-left → down jagged edge → bottom-left
        const leftPoly = ['0 0'];
        for (const p of jagPoints) { leftPoly.push(`${p.x}px ${p.y}px`); }
        leftPoly.push('0 100%');
        const leftClip = `polygon(${leftPoly.join(', ')})`;

        // Right half: top-right → down jagged edge (reversed) → bottom-right
        const rightPoly = ['100% 0'];
        for (const p of jagPoints) { rightPoly.push(`${p.x}px ${p.y}px`); }
        rightPoly.push('100% 100%');
        const rightClip = `polygon(${rightPoly.join(', ')})`;

        function makeHalf(clip: string): HTMLElement {
            const half = el.cloneNode(true) as HTMLElement;
            half.style.cssText = `
                position:fixed;
                left:${rect.left}px;
                top:${rect.top}px;
                width:${rect.width}px;
                height:${rect.height}px;
                clip-path:${clip};
                pointer-events:none;
            `;
            container.appendChild(half);
            return half;
        }

        const leftHalf = makeHalf(leftClip);
        const rightHalf = makeHalf(rightClip);

        // Snap apart — left tilts left, right tilts right
        const snapAnim1 = leftHalf.animate([
            { transform: 'translate(0, 0) rotate(0deg)' },
            { transform: 'translate(-10px, 3px) rotate(-3deg)' },
        ], { duration: 350, easing: 'cubic-bezier(0.2, 0, 1, 1)', fill: 'forwards' });

        const snapAnim2 = rightHalf.animate([
            { transform: 'translate(0, 0) rotate(0deg)' },
            { transform: 'translate(10px, 3px) rotate(3deg)' },
        ], { duration: 350, easing: 'cubic-bezier(0.2, 0, 1, 1)', fill: 'forwards' });

        // Phase 2: After snap, explode each half into shards
        return Promise.all([snapAnim1.finished, snapAnim2.finished]).then(() => {
            return new Promise<void>((resolve) => {
                setTimeout(() => {
                    leftHalf.remove();
                    rightHalf.remove();

                    const cols = 4;
                    const rows = 4;
                    const pieceW = rect.width / cols;
                    const pieceH = rect.height / rows;
                    const animations: Animation[] = [];

                    for (let r = 0; r < rows; r++) {
                        for (let c = 0; c < cols; c++) {
                            const shard = el.cloneNode(true) as HTMLElement;
                            const clipTop = r * pieceH;
                            const clipRight = rect.width - (c + 1) * pieceW;
                            const clipBottom = rect.height - (r + 1) * pieceH;
                            const clipLeft = c * pieceW;

                            const isLeft = (c + 0.5) * pieceW < midX;
                            const startDx = isLeft ? -10 : 10;
                            const startRot = isLeft ? -3 : 3;

                            shard.style.cssText = `
                                position:fixed;
                                left:${rect.left}px;
                                top:${rect.top}px;
                                width:${rect.width}px;
                                height:${rect.height}px;
                                clip-path:inset(${clipTop}px ${clipRight}px ${clipBottom}px ${clipLeft}px);
                                pointer-events:none;
                            `;
                            container.appendChild(shard);

                            const centerX = (c + 0.5) / cols - 0.5;
                            const centerY = (r + 0.5) / rows - 0.5;
                            const spread = 300 + Math.random() * 300;
                            const dx = centerX * spread + (Math.random() - 0.5) * 150;
                            const dy = centerY * spread - 100 - Math.random() * 200;
                            const rotation = (Math.random() - 0.5) * 540;

                            const anim = shard.animate([
                                { transform: `translate(${startDx}px, 2px) rotate(${startRot}deg) scale(1)`, opacity: 1 },
                                { transform: `translate(${dx}px, ${dy + 400}px) rotate(${rotation}deg) scale(0.2)`, opacity: 0 },
                            ], {
                                duration: 900 + Math.random() * 400,
                                easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
                                fill: 'forwards',
                            });
                            animations.push(anim);
                        }
                    }

                    Promise.all(animations.map(a => a.finished)).then(() => {
                        container.remove();
                        resolve();
                    });
                }, 300);
            });
        });
    }

    function confirmRemove() {
        if (!removeScheduleId) {
            return;
        }
        const id = removeScheduleId;
        removeScheduleId = null;

        // On desktop both mobile (md:hidden) and desktop (hidden md:grid) cards exist;
        // pick the one that's actually visible so the explosion clones render correctly.
        const candidates = document.querySelectorAll(`[data-schedule-id="${id}"]`);
        const cardEl = Array.from(candidates).find(
            (el) => (el as HTMLElement).offsetParent !== null
        ) as HTMLElement | null;
        const doDelete = () => {
            router.delete(remove(id), {
                preserveScroll: true,
                only: ['schedule'],
                invalidateCacheTags: ['admin-cases'],
            });
        };

        if (cardEl) {
            explodeElement(cardEl).then(doDelete);
        } else {
            doDelete();
        }
    }
</script>

<style>
    .past-day {
        background-image: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 5px,
            rgba(255, 255, 255, 0.04) 5px,
            rgba(255, 255, 255, 0.04) 6px
        );
    }

    @keyframes pop-in {
        0% {
            opacity: 0;
            transform: scale(0.8);
        }
        50% {
            transform: scale(1.03);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    }

    :global(.animate-pop-in) {
        animation: pop-in 0.4s ease-out;
    }

</style>

<svelte:head>
    <title>Schedule — Admin — Case of the Day</title>
</svelte:head>

<div class="flex items-center justify-between gap-4 mb-6">
    <h1 class="text-2xl font-bold text-gray-100 shrink-0">Schedule</h1>
        <!-- Mobile: pill-styled dropdown -->
        <div class="md:hidden relative">
            <select
                class="appearance-none rounded-full pl-3 pr-8 py-1 text-xs font-medium border-0 focus:ring-2 focus:ring-cyan-400 focus:ring-offset-1 focus:ring-offset-gray-950 focus:outline-none {topicId !== null ? 'bg-cyan-600 text-white' : 'bg-cyan-900 text-cyan-300'}"
                value={topicId ?? ''}
                onchange={(e) => filterByTopic(e.currentTarget.value ? Number(e.currentTarget.value) : null)}
            >
                <option value="">All Topics</option>
                {#each topics as topic (topic.id)}
                    <option value={topic.id} disabled={topic.disabled}>{topic.name}{topic.disabled ? ' (off)' : ''}</option>
                {/each}
            </select>
            <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 size-3 {topicId !== null ? 'text-white/70' : 'text-cyan-300/70'}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor"><path d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"/></svg>
        </div>
        <!-- Desktop: pill buttons -->
        <div class="hidden md:flex items-center gap-2 flex-wrap justify-end">
            <button
                onclick={() => filterByTopic(null)}
                class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors {topicId === null ? 'bg-cyan-600 text-white ring-2 ring-cyan-400 ring-offset-1 ring-offset-gray-950' : 'bg-cyan-900 text-cyan-300 hover:bg-cyan-800'}"
            >All</button>
            {#each topics as topic (topic.id)}
                <button
                    onclick={() => filterByTopic(topic.id)}
                    class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors {topicId === topic.id ? 'bg-cyan-600 text-white ring-2 ring-cyan-400 ring-offset-1 ring-offset-gray-950' : topic.disabled ? 'bg-gray-800 text-gray-500 hover:bg-gray-700' : 'bg-cyan-900 text-cyan-300 hover:bg-cyan-800'}"
                >
                    {topic.name}{#if topic.disabled} <span class="ml-0.5 opacity-60">(off)</span>{/if}
                </button>
            {/each}
        </div>
    </div>

    <!-- Mobile agenda view -->
    <div class="md:hidden space-y-0 rounded-lg overflow-clip shadow ring-1 ring-gray-700">
        <button
            onclick={() => { if (hiddenPastCount >= 7) { pastDaysToShow += 7; } else { loadEarlier(7); } }}
            class="w-full px-3 py-2 text-xs text-gray-500 bg-gray-900 hover:bg-gray-800 transition-colors flex items-center justify-center gap-1.5"
        >
            <svg class="size-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120l0 136c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2 280 120c0-13.3-10.7-24-24-24s-24 10.7-24 24z"/></svg>
            Load previous week
        </button>
        {#if pastDaysToShow > 0}
            <button
                onclick={() => pastDaysToShow = 0}
                class="w-full px-3 py-2 text-xs text-gray-500 bg-gray-900 hover:bg-gray-800 transition-colors flex items-center justify-center gap-1.5"
            >
                <svg class="size-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
                Hide past days
            </button>
        {/if}
        {#each agendaMonths() as month (month.label)}
            <div class="sticky top-0 z-10 bg-gray-800 px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {month.label}
            </div>
            {#each month.dates as date (date)}
                {@const info = agendaDayLabel(date)}
                {@const cases = scheduleData[date] ?? []}
                <div
                    class="flex gap-3 px-3 py-2.5 border-b border-gray-800 {date === today ? 'bg-cyan-950/40 ring-1 ring-inset ring-cyan-500/50' : info.isWeekend ? 'bg-gray-950' : 'bg-gray-900'} {isPast(date) ? 'past-day' : ''}"
                >
                    <!-- Date column -->
                    <div class="w-10 shrink-0 text-center pt-0.5">
                        <div class="text-lg font-bold leading-tight {date === today ? 'text-cyan-400' : isPast(date) ? 'text-gray-600' : 'text-gray-300'}">{info.day}</div>
                        <div class="text-[10px] uppercase tracking-wide {date === today ? 'text-cyan-400/70' : isPast(date) ? 'text-gray-700' : 'text-gray-500'}">{info.weekday}</div>
                    </div>
                    <!-- Cases column -->
                    <div class="flex-1 min-w-0">
                        {#if cases.length === 0}
                            {#if !isPast(date)}
                                <button
                                    onclick={() => openAddModal(date)}
                                    class="flex items-center gap-1.5 w-full py-1.5 text-xs text-gray-500 hover:text-cyan-400 transition-colors"
                                >
                                    <svg class="size-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z"/></svg>
                                    Add case
                                </button>
                            {:else}
                                <div class="py-1.5 text-xs text-gray-700">—</div>
                            {/if}
                        {:else}
                            <div class="space-y-1.5">
                                {#each cases as c (c.schedule_id)}
                                    <div
                                        class="group/card relative {isNew(c.schedule_id) ? 'animate-pop-in' : ''}"
                                        data-schedule-id={c.schedule_id}
                                    >
                                        <Link
                                            href={show(c.id)}
                                            class="flex items-center gap-2.5 rounded-lg border border-gray-700 bg-gray-800 p-2 transition-colors hover:border-gray-600 {isPast(date) ? 'opacity-40' : ''}"
                                        >
                                            {#if c.image_url}
                                                <img src={c.image_url} alt="" class="size-10 rounded object-cover shrink-0 bg-gray-700" />
                                            {:else}
                                                <div class="size-10 rounded bg-gray-700 shrink-0"></div>
                                            {/if}
                                            <div class="min-w-0 flex-1">
                                                <p class="text-sm text-gray-100 line-clamp-2">{c.history_excerpt}</p>
                                                {#if c.topic_name}
                                                    <span class="inline-flex items-center rounded-full bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 mt-1">{c.topic_name}</span>
                                                {/if}
                                            </div>
                                        </Link>
                                        {#if !isPast(date)}
                                            <button
                                                onclick={(e) => promptRemove(e, c.schedule_id, date)}
                                                class="absolute top-1.5 right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-red-500/80 text-white text-xs font-bold leading-none hover:bg-red-600 transition-colors cursor-pointer shadow-sm"
                                                title="Remove from schedule"
                                            >&times;</button>
                                        {/if}
                                    </div>
                                {/each}
                                {#if !isPast(date)}
                                    <button
                                        onclick={() => openAddModal(date)}
                                        class="flex items-center gap-1 text-[11px] text-gray-500 hover:text-cyan-400 transition-colors pl-1"
                                    >
                                        <svg class="size-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z"/></svg>
                                        Add
                                    </button>
                                {/if}
                            </div>
                        {/if}
                    </div>
                </div>
            {/each}
        {/each}
        <button
            onclick={() => { if (remainingDays < 7) { loadMore(7); } agendaDaysToShow += 7; }}
            class="w-full px-3 py-3 text-sm font-medium text-gray-400 bg-gray-900 hover:bg-gray-800 transition-colors"
        >
            Load next week
        </button>
    </div>

    <!-- Desktop calendar grid -->
    <div class="hidden md:grid grid-cols-7 gap-px bg-gray-800 rounded-lg overflow-hidden shadow ring-1 ring-gray-700">
        {#each dayNames as day (day)}
            <div class="bg-gray-950 text-center py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {day}
            </div>
        {/each}
        {#each weeks() as week, weekIndex (weekIndex)}
            {#if newMonthInWeek(week, weekIndex, weeks())}
                <div class="col-span-7 bg-gray-800 px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {newMonthInWeek(week, weekIndex, weeks())}
                </div>
            {/if}
            {@const sectionMonth = sectionMonthForWeek(weekIndex, weeks())}
            {#each week as date, i (date)}
                <div
                    class="group/cell relative min-h-[110px] p-2 transition-colors {i >= 5 ? 'bg-gray-950' : 'bg-gray-900'} {date === today ? 'ring-2 ring-inset ring-cyan-500' : ''} {isPast(date) ? 'past-day' : ''} {dropTargetDate === date ? 'bg-cyan-950 ring-2 ring-inset ring-cyan-400' : ''} {dragScheduleId && !isValidDropTarget(date) && date !== dragFromDate ? 'opacity-40' : ''}"
                    ondragover={(e) => handleDragOver(e, date)}
                    ondragleave={(e) => handleDragLeave(e, date)}
                    ondrop={(e) => handleDrop(e, date)}
                    role="group"
                >
                    <div class="flex items-center justify-between mb-1.5">
                        <span class="text-xs font-medium {date === today ? 'text-cyan-400' : isPast(date) ? 'text-gray-600' : 'text-gray-400'}">
                            {formatDate(date, sectionMonth)}
                        </span>
                        {#if !isPast(date) && !dragScheduleId}
                            <button
                                onclick={() => openAddModal(date)}
                                class="flex items-center justify-center w-5 h-5 rounded bg-cyan-600 text-white text-xs font-bold leading-none hover:bg-cyan-500 transition-colors cursor-pointer opacity-0 group-hover/cell:opacity-100"
                                title="Add case on {formatDateFull(date)}"
                            >+</button>
                        {/if}
                    </div>
                    <div class="space-y-1">
                        {#each scheduleData[date] ?? [] as c (c.schedule_id)}
                            <div
                                class="group/card relative {isNew(c.schedule_id) ? 'animate-pop-in' : ''}"
                                data-schedule-id={c.schedule_id}
                                in:scale={{ duration: 500, delay: 100, start: 0.8 }}
                                animate:flip={{ duration: 500 }}
                            >
                                <Link
                                    href={show(c.id)}
                                    class="relative block overflow-hidden rounded border border-gray-700 bg-gray-800 text-xs leading-tight hover:shadow transition-shadow {dragScheduleId === c.schedule_id ? 'opacity-50' : ''} {isPast(date) ? 'cursor-default opacity-40' : 'cursor-grab active:cursor-grabbing'}"
                                    draggable={!isPast(date)}
                                    ondragstart={(e) => handleDragStart(e, c, date)}
                                    ondragend={handleDragEnd}
                                >
                                    {#if c.image_url}
                                        <img src={c.image_url} alt="" class="aspect-4/3 w-full object-cover" />
                                    {:else}
                                        <div class="aspect-4/3 w-full"></div>
                                    {/if}
                                    {#if c.topic_name}
                                        <span class="absolute top-1 right-1 rounded-full bg-gray-900/90 text-gray-300 px-1.5 py-0.5 text-[10px] font-medium leading-none shadow-sm">{c.topic_name}</span>
                                    {/if}
                                    <div class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 pt-4 pb-1">
                                        <p class="truncate text-white/90">{c.history_excerpt}</p>
                                    </div>
                                </Link>
                                {#if !dragScheduleId && !isPast(date)}
                                    <button
                                        onclick={(e) => promptRemove(e, c.schedule_id, date)}
                                        class="absolute top-1 left-1 hidden group-hover/card:flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold leading-none hover:bg-red-600 transition-colors cursor-pointer shadow-sm"
                                        title="Remove from schedule"
                                    >&times;</button>
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>
            {/each}
        {/each}
    </div>

    <div class="hidden md:block mt-4 text-center">
        <button
            onclick={() => loadMore()}
            class="px-4 py-2 text-sm font-medium text-gray-400 bg-gray-900 border border-gray-700 rounded-md hover:bg-gray-800 transition-colors cursor-pointer"
        >Load 4 more weeks</button>
    </div>

<Modal bind:open={modalOpen} title="Add case on {formatDateFull(selectedDate)}">
    <div class="space-y-4">
        <input
            type="text"
            bind:value={searchQuery}
            oninput={handleSearchInput}
            placeholder="Search approved cases..."
            class="w-full border border-gray-700 bg-gray-800 text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
        />

        <div class="max-h-64 overflow-y-auto space-y-1">
            {#if searching}
                <p class="text-sm text-gray-400 text-center py-4">Searching...</p>
            {:else if searchResults.length === 0}
                <p class="text-sm text-gray-400 text-center py-4">No approved cases found.</p>
            {:else}
                {#each searchResults as result (result.id)}
                    {@const alreadyScheduled = result.scheduled_dates.includes(selectedDate)}
                    <button
                        onclick={() => scheduleCase(result.id)}
                        disabled={scheduling || alreadyScheduled}
                        class="w-full text-left rounded border border-gray-700 px-3 py-2 text-sm transition-colors {alreadyScheduled ? 'past-day opacity-60 cursor-not-allowed' : 'hover:bg-cyan-950 hover:border-cyan-500 cursor-pointer'} {scheduling ? 'opacity-50' : ''}"
                    >
                        <span class="font-medium text-gray-100">#{result.id}</span>
                        {#if result.topic_name}
                            <span class="text-xs text-gray-500">· {result.topic_name}</span>
                        {/if}
                        <p class="text-gray-400 truncate mt-0.5">{result.history_excerpt}</p>
                        {#if result.scheduled_dates.length > 0}
                            <p class="text-[10px] text-gray-400 mt-1">
                                {alreadyScheduled ? 'Already on this date' : `Scheduled: ${result.scheduled_dates.join(', ')}`}
                            </p>
                        {/if}
                    </button>
                {/each}
            {/if}
        </div>
    </div>
</Modal>

<ConfirmModal
    bind:open={removeConfirmOpen}
    title={removeConfirmTitle}
    message={removeConfirmMessage}
    confirmLabel="Remove"
    variant={removeVariant}
    panelClass={removeVariant === 'danger' ? 'danger-glow' : ''}
    onconfirm={confirmRemove}
    oncancel={() => { removeScheduleId = null; }}
/>

<ConfirmModal
    bind:open={moveConfirmOpen}
    title="Move today's case?"
    message="This case is scheduled for TODAY. Users may have already seen it and submitted answers. Moving it will remove it from today's schedule."
    confirmLabel="Move"
    variant="danger"
    panelClass="danger-glow"
    onconfirm={confirmMove}
    oncancel={() => { pendingMoveScheduleId = null; }}
/>
